import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { Dialog } from "primereact/dialog";

import { doc, getDoc } from "firebase/firestore";
import { db, getFirebaseImageUrl } from "../firebase";

import { store } from "../store";

import { numberWithCommas } from "../CustomMath";

import "assets/css/item.css";
import { useTranslation } from "language-maps/use-translation";
import { getActiveDiscount } from "../discountService";
import { getDesignDiscountsMap } from "../designDiscountService";
import { blindSubCategorySlugs, slugToName } from "../categoryMappings";

const defaultState = {
  activeIndex: 0,
  selectedDesign: {},
  image: "",
  type: "Door",
  fabricPrice: 0,
  price: 850,
  width: 36,
  height: 100,
  pleats: 9,
  frontPleats: 12,
  backPleats: 18,
  cost: 0,
  quantity: 1,
  total: 0,
  isPole: false,
  fabricCost: 0,
  baseTotal: 0,
};

const defaultBlindState = {
  selectedDesign: {},
  image: "",
  price: 0,
  type: "Double Window",
  width: 48,
  height: 60,
  wallMount: "Inside",
  pullCordSide: "Right",
  frameColor: "A suitable one",
  bracket: "Internal",
  bracketFix: "Wall",
  quantity: 1,
  cost: 0,
  total: 0,
};

const blindMinSqftBySubCategory = {
  zebrablinds: 16,
  verticalblinds: 20,
  rollerblinds: 16,
  romanblinds: 16,
  bambooblinds: 16,
  venetianblinds: 16,
};
const DEFAULT_BLIND_MIN_SQFT = 16;

// Interior Designs are finished, fixed-size products (the size is part of the
// product name). They are sold per item — flat price x quantity — so they skip
// the curtain/blind measurement formulas entirely.
const INTERIOR_CATEGORY = "Interior Designs";

// Default height (inches) per blind sub-category
const blindDefaultHeightBySubCategory = {
  zebrablinds: 48,
  verticalblinds: 60,
  rollerblinds: 48,
  romanblinds: 48,
  bambooblinds: 48,
  venetianblinds: 48,
};
const DEFAULT_BLIND_HEIGHT = 48;

const types = [
  { name: "Door", value: 36 },
  { name: "Single Window", value: 24 },
  { name: "Double Window", value: 48 },
  { name: "Triple Window", value: 72 },
  { name: "Quadruple Window", value: 96 },
  { name: "Other", value: 120 },
];

const Item = () => {
  const [state, setState] = useState({ ...defaultState });
  const { id } = useParams();
  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState(false);
  const { t } = useTranslation();
  const [showLimitDialog, setShowLimitDialog] = useState(false);
  const [discount, setDiscount] = useState(null);
  const [designDiscount, setDesignDiscount] = useState(null);

  const [blindState, setBlindState] = useState({ ...defaultBlindState });

  const [showWidthDesc, setShowWidthDesc] = useState(false);
  const [showHeightDesc, setShowHeightDesc] = useState(false);
  const [showMountDesc, setShowMountDesc] = useState(false);
  const [showPleatsDesc, setShowPleatsDesc] = useState(false);
  const [showTypeDesc, setShowTypeDesc] = useState(false);
  const [showPoleDesc, setShowPoleDesc] = useState(false);

  const checkTypeLimit = (type) => {
    // Check if user is logged in by looking for token in localStorage
    const token = localStorage.getItem('token');
    if (token) {
      // Skip limit check for logged-in users
      return true;
    }

    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const storageKey = `typeUsage_${currentMonth}_${currentYear}`;
    
    // Get existing usage data from localStorage
    const existingData = JSON.parse(localStorage.getItem(storageKey) || '{}');
    
    // Initialize if not exists
    if (!existingData[type]) {
      existingData[type] = 0;
    }
    
    // Check if limit reached
    if (existingData[type] >= 15) {
      setShowLimitDialog(true);
      return false;
    }
    
    // Increment usage
    existingData[type]++;
    localStorage.setItem(storageKey, JSON.stringify(existingData));
    return true;
  };

  const calculateBlind = (bs) => {
    const sqFt = (bs.width * bs.height) / 144;
    const subCat = bs.selectedDesign?.SubCategory;
    const minSqft = blindMinSqftBySubCategory[subCat] ?? DEFAULT_BLIND_MIN_SQFT;
    const unitCost = sqFt < minSqft ? minSqft * bs.price : sqFt * bs.price;
    const total = unitCost * bs.quantity;
    setBlindState({ ...bs, cost: unitCost, total });
  };

  const onBlindChange = (name, value) => {
    const updated = { ...blindState, [name]: value };
    calculateBlind(updated);
  };

  const onBlindTypeChange = (value) => {
    if (!checkTypeLimit(value)) return;
    const type = types.find((t) => t.name === value);
    if (!type) return;
    const updated = { ...blindState, type: type.name, width: type.value };
    calculateBlind(updated);
  };

  const loadData = useCallback(async () => {
    const docRef = doc(db, "Designs", id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      const isBlind =
        data.Category === "Blinds" ||
        blindSubCategorySlugs.includes(data.SubCategory);

      if (data.Category === INTERIOR_CATEGORY) {
        // Finished product: quantity only, no measurements.
        calculateInterior({
          ...defaultState,
          selectedDesign: data,
          image: data.Image,
          price: data.Price,
          quantity: 1,
          type: "Interior Design",
        });
      } else if (isBlind) {
        const defaultHeight = blindDefaultHeightBySubCategory[data.SubCategory] ?? DEFAULT_BLIND_HEIGHT;
        const bs = {
          ...defaultBlindState,
          height: defaultHeight,
          selectedDesign: data,
          image: data.Image,
          price: data.Price,
        };
        calculateBlind(bs);
        setState((prev) => ({ ...prev, selectedDesign: data, image: data.Image, price: data.Price }));
      } else {
        const isDouble = data.Category === "Double Curtains";
        const defaultWithPole = data.Category === "Ripple Curtains" || isDouble;
        const currentState = {
          ...state,
          selectedDesign: data,
          image: data.Image,
          price: data.Price,
          isPole: defaultWithPole ? true : state.isPole,
          // Door type is hidden for double curtains — fall back to a window default
          type: isDouble && state.type === "Door" ? "Single Window" : state.type,
          width: isDouble && state.type === "Door" ? 24 : state.width,
        };
        calculate(currentState);
      }
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    getActiveDiscount().then((d) => setDiscount(d));
  }, []);

  useEffect(() => {
    if (id) {
      getDesignDiscountsMap([id]).then((map) => {
        const d = map[id];
        setDesignDiscount(d || null);
      });
    }
  }, [id]);

  const onTypeChange = (value) => {
    if (!checkTypeLimit(value)) {
      return;
    }
    
    const type = types.find((t) => t.name === value);
    if (!type) return;
    const temp = {
      ...state,
      type: type.name,
      width: type.value,
    };

    calculate(temp, true);
  };

  const onPoleChange = (e) => {
    const temp = { ...state, isPole: e.value };
    calculate(temp, false);
  };

  const onChange = (name, value, getPleats = true) => {
    const temp = { ...state, [name]: value };
    calculate(temp, getPleats);
  };

  const getRecommended = (currentState) => {
    let pleats = Math.round((currentState?.width ?? state.width) / 3.92);

    if (
      pleats % 2 !== 0 &&
      !(
        (currentState?.type ?? state.type) === "Door" ||
        (currentState?.type ?? state.type) === "Single Window"
      )
    ) {
      pleats += 1;
    }

    return pleats;
  };

  // Double curtain pleat formula: MROUND(width * 5/12, 2) — round to nearest even
  const getRecommendedDouble = (currentState) => {
    const raw = (currentState?.width ?? state.width) * 5 / 12;
    return Math.round(raw / 2) * 2; // round to nearest even
  };

  // Ripple curtain pleat recommendation: MROUND(width / 3.5, 2)
  const getRecommendedRipple = (currentState) => {
    const raw = (currentState?.width ?? state.width) / 3.5;
    return Math.round(raw / 2) * 2; // round to nearest even
  };

  // Finished product sold per item: no measurements, just price x quantity.
  const calculateInterior = (currentState) => {
    const cost = currentState.price;
    const total = cost * currentState.quantity;
    setState({
      ...currentState,
      cost,
      total,
      baseTotal: total,
      width: 0,
      height: 0,
      pleats: 0,
      isPole: false,
      fabricCost: 0,
      fabricPrice: 0,
    });
  };

  const calculate = (currentState, getPleats = true) => {
    // Interior Designs are priced per item — no measurement maths.
    if (currentState.selectedDesign?.Category === INTERIOR_CATEGORY) {
      calculateInterior(currentState);
      return;
    }

    // Route to double curtain formula if applicable
    if (currentState.selectedDesign?.Category === "Double Curtains") {
      calculateDoubleCurtain(currentState, getPleats);
      return;
    }

    // Ripple curtain spec formula
    if (currentState.selectedDesign?.Category === "Ripple Curtains") {
      calculateRipple(currentState, getPleats);
      return;
    }

    const pleats = getPleats
      ? getRecommended(currentState)
      : currentState.pleats;

    const price = currentState.price;
    let fabricPrice = price;

    if (currentState.height > 102) fabricPrice += price * 0.1;
    if (currentState.height < 48) fabricPrice = price / 2;
    const fabricCost =
      currentState.quantity *
      fabricPrice *
      (Math.round(((pleats * 14) / 39) * 100) / 100);
    const barCost =
      (currentState.width < 48 ? 5 : currentState.width / 12 + 1) * 300;
    const multiplier = 1.03 * 1.18 * 1.082;
    const curtainComponents =
      ((pleats * 14) / 39) * (fabricPrice + 65) +
      pleats * 60 +
      pleats * 2 * 46;
    let cost = (barCost + curtainComponents) * multiplier;

    const baseCost = cost - barCost * 1.25;
    if (!currentState.isPole) cost = baseCost;
    const total = +cost * currentState.quantity;
    const baseTotal = +baseCost * currentState.quantity;

    setState(() => ({
      ...currentState,
      pleats,
      fabricPrice,
      fabricCost,
      cost,
      total,
      baseTotal,
    }));
  };

  // Ripple curtain spec (breakdown table):
  //   barQty     = IF(width < 48, 5, width/12 + 1)
  //   barCost    = barQty × 550                          (rate 550, common)
  //   fabricMtr  = pleats × 13 / 39
  //   fabricAmt  = fabricMtr × fabricPrice               (Price = per-item rate)
  //   stiffAmt   = fabricMtr × 65                        (rate 65, common)
  //   sewingAmt  = pleats × 60                           (rate 60, common)
  //   eyeletAmt  = 0                                     (qty pleats×2, no rate)
  //   totalCost  = bar + fabric + stiff + sewing + eyelet
  //   withPole    = totalCost × 1.03 × 1.18 × 1.082      (wastage → admin → capital)
  //   withoutPole = withPole − barCost × 1.25
  //   heightFactor = 1 + 0.05 × ceil(max(height − 102, 0) / 10)
  //   cost  = (isPole ? withPole : withoutPole) × heightFactor
  //   total = cost × quantity
  const calculateRipple = (currentState, getPleats = true) => {
    const pleats = getPleats
      ? getRecommendedRipple(currentState)
      : currentState.pleats;

    const fabricPrice = currentState.price;

    const barQty = currentState.width < 48 ? 5 : currentState.width / 12 + 1;
    const barCost = barQty * 550;
    const fabricMtr = (pleats * 13) / 39;
    const fabricAmt = fabricMtr * fabricPrice;
    const stiffAmt = fabricMtr * 65;
    const sewingAmt = pleats * 60;
    const eyeletAmt = 0;

    const totalCost = barCost + fabricAmt + stiffAmt + sewingAmt + eyeletAmt;
    const multiplier = 1.03 * 1.18 * 1.082;
    const withPole = totalCost * multiplier;
    const withoutPole = withPole - barCost * 1.25;

    const heightExcess = Math.max(currentState.height - 102, 0);
    const heightFactor = 1 + 0.05 * Math.ceil(heightExcess / 10);

    const cost = (currentState.isPole ? withPole : withoutPole) * heightFactor;
    const baseCost = withoutPole * heightFactor;
    const total = cost * currentState.quantity;
    const baseTotal = baseCost * currentState.quantity;
    const fabricCost = currentState.quantity * fabricAmt;

    setState(() => ({
      ...currentState,
      pleats,
      fabricPrice,
      fabricCost,
      cost,
      total,
      baseTotal,
    }));
  };

  // Double curtain spec (breakdown table):
  //   barQty     = IF(width < 48, 5, width/12 + 1)
  //   barCost    = barQty × 300 × 1.5                     (rate 300, ×1.5 double pole)
  //   fabricMtr  = pleats × 14 / 39
  //   fabricAmt  = fabricMtr × fabricPrice
  //   stiffAmt   = fabricMtr × 65
  //   sewingAmt  = pleats × 60
  //   eyeletAmt  = pleats × 2 × 46
  //   totalCost  = bar + fabric + stiff + sewing + eyelet
  //   withPole    = totalCost × 1.03 × 1.18 × 1.082
  //   withoutPole = withPole − barCost × 1.5
  //   heightFactor = 1 + 0.05 × ceil(max(height − 102, 0) / 10)
  //   cost  = (isPole ? withPole : withoutPole) × heightFactor
  //   total = cost × quantity
  const calculateDoubleCurtain = (currentState, getPleats = true) => {
    let frontPleats = currentState.frontPleats;
    let backPleats = currentState.backPleats;
    if (getPleats) {
      const total = getRecommendedDouble(currentState);
      frontPleats = Math.max(2, Math.round(total * 0.4));
      backPleats = Math.max(2, total - frontPleats);
    }
    const pleats = (frontPleats || 0) + (backPleats || 0);

    const fabricPrice = currentState.price;

    const barQty = currentState.width < 48 ? 5 : currentState.width / 12 + 1;
    const barCost = barQty * 300 * 1.5;
    const fabricMtr = (pleats * 14) / 39;
    const fabricAmt = fabricMtr * fabricPrice;
    const stiffAmt = fabricMtr * 65;
    const sewingAmt = pleats * 60;
    const eyeletAmt = pleats * 2 * 46;

    const totalCost = barCost + fabricAmt + stiffAmt + sewingAmt + eyeletAmt;
    const multiplier = 1.03 * 1.18 * 1.082;
    const withPole = totalCost * multiplier;
    const withoutPole = withPole - barCost * 1.5;

    const heightExcess = Math.max(currentState.height - 102, 0);
    const heightFactor = 1 + 0.05 * Math.ceil(heightExcess / 10);

    const cost = (currentState.isPole ? withPole : withoutPole) * heightFactor;
    const baseCost = withoutPole * heightFactor;
    const total = cost * currentState.quantity;
    const baseTotal = baseCost * currentState.quantity;
    const fabricCost = currentState.quantity * fabricAmt;

    setState(() => ({
      ...currentState,
      pleats,
      frontPleats,
      backPleats,
      fabricPrice,
      fabricCost,
      cost,
      total,
      baseTotal,
    }));
  };

  const isBlindsItem = () => {
    const design = state.selectedDesign;
    if (!design) return false;
    return (
      design.Category === "Blinds" ||
      blindSubCategorySlugs.includes(design.SubCategory)
    );
  };

  const isInteriorItem = () =>
    state.selectedDesign?.Category === INTERIOR_CATEGORY;

  const isDoubleCurtainItem = () => {
    const design = state.selectedDesign;
    if (!design) return false;
    return design.Category === "Double Curtains";
  };

  const getEffectiveDiscount = () => designDiscount || discount;

  const getEffectiveDiscountAmount = (total) => {
    const effective = getEffectiveDiscount();
    if (!effective || !total) return 0;
    const raw = effective.isPercentage
      ? (total * effective.value) / 100
      : Math.min(effective.value, total);
    return Math.round(raw * 100) / 100;
  };

  const getEffectiveDiscountLabel = () => {
    const effective = getEffectiveDiscount();
    if (!effective) return "";
    if (designDiscount) {
      return designDiscount.isPercentage
        ? `Item discount (${designDiscount.value}%)`
        : "Item discount";
    }
    return discount?.name || "Discount";
  };

  const addToCart = () => {
    if (isBlindsItem()) {
      const itemDiscountAmount = getEffectiveDiscountAmount(blindState.total);
      store.dispatch({
        type: "itemAdded",
        payload: {
          ...blindState,
          type: "Blind",
          isPole: false,
          pleats: 0,
          fabricCost: 0,
          fabricPrice: 0,
          designDiscountAmount: itemDiscountAmount,
          discountName: getEffectiveDiscountLabel(),
        },
      });
    } else {
      const itemDiscountAmount = getEffectiveDiscountAmount(state.total);
      store.dispatch({
        type: "itemAdded",
        payload: {
          ...state,
          designDiscountAmount: itemDiscountAmount,
          discountName: getEffectiveDiscountLabel(),
        },
      });
    }
    setShowDialog(true);
  };

  const reset = () => {
    if (isInteriorItem()) {
      calculateInterior({
        ...defaultState,
        selectedDesign: state.selectedDesign,
        image: state.image,
        price: state.price,
        quantity: 1,
        type: "Interior Design",
      });
    } else if (isBlindsItem()) {
      const sub = blindState.selectedDesign?.SubCategory;
      const defaultHeight = blindDefaultHeightBySubCategory[sub] ?? DEFAULT_BLIND_HEIGHT;
      const bs = {
        ...defaultBlindState,
        height: defaultHeight,
        selectedDesign: blindState.selectedDesign,
        image: blindState.image,
        price: blindState.price,
      };
      calculateBlind(bs);
    } else {
      const cat = state.selectedDesign?.Category;
      const isDouble = cat === "Double Curtains";
      const defaultWithPole = cat === "Ripple Curtains" || isDouble;
      const temp = {
        ...defaultState,
        selectedDesign: state.selectedDesign,
        image: state.image,
        price: state.price,
        isPole: defaultWithPole ? true : defaultState.isPole,
        type: isDouble && defaultState.type === "Door" ? "Single Window" : defaultState.type,
        width: isDouble && defaultState.type === "Door" ? 24 : defaultState.width,
      };
      setState(temp);
      calculate(temp);
    }
    setShowDialog(false);
  };

  // Interior Designs come in a fixed size (stated in the item code), so there is
  // nothing to measure — the customer only picks how many they want.
  const renderInteriorCalculator = () => (
    <div className="col-12 lg:col-7 lg:pl-3">
      <h1 className="text-4xl lg:text-6xl line-height-1 mt-5 lg:mt-7 cormorant-garamond">
        {t("Select Quantity")}
      </h1>
      <p className="mt-3">{t("This is a finished product supplied in a fixed size.")}</p>
      <div className="grid">
        <div className="col-12 lg:col-7 gap-2 mt-2">
          <div className="mt-3">
            <div>{t("Quantity")}</div>
            <InputNumber
              className="w-full lg:w-7rem mt-2"
              min={1}
              id="quantity"
              name="quantity"
              value={state.quantity}
              onChange={(e) => onChange("quantity", e.value, false)}
              showButtons
              buttonLayout="horizontal"
              step={1}
              decrementButtonClassName="p-button-primary"
              incrementButtonClassName="p-button-primary"
              incrementButtonIcon="pi pi-plus"
              decrementButtonIcon="pi pi-minus"
            />
          </div>
        </div>
        <div className="col-12 lg:col-5 mt-4">
          <div className="border-dashed border-500 justify-content-center p-2 flex flex-column gap-2">
            <strong>
              {t("Price per unit")} : LKR {numberWithCommas(state.cost)}
            </strong>
            {getEffectiveDiscount() && (
              <span>
                {getEffectiveDiscountLabel()} : - LKR{" "}
                {numberWithCommas(getEffectiveDiscountAmount(state.total))}
              </span>
            )}
            <strong>
              {t("Total")} : LKR{" "}
              {numberWithCommas(state.total - getEffectiveDiscountAmount(state.total))}
            </strong>
          </div>
          <div>
            <Button
              className="surface-700 mt-3 w-full"
              label={t("Add to Cart")}
              icon="pi pi-shopping-cart"
              onClick={() => addToCart()}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderBlindsCalculator = () => (
    <div className="col-12 lg:col-7 lg:pl-3">
      <h1 className="text-4xl lg:text-6xl line-height-1 mt-5 lg:mt-7 cormorant-garamond">
        {t("Customize Selected Design")}
      </h1>
      <div className="flex align-items-center mt-3">
        {t("Type")}
        <Button
          className="p-button-help ml-2"
          icon="pi pi-info-circle"
          label={t("View Description")}
          onClick={() => setShowTypeDesc(true)}
        />
      </div>
      <div className="design-types row">
        {types.filter((tp) => tp.name !== "Door").map((tp) => {
          const slugMap = {
            "Single Window": "single-window.jpg",
            "Double Window": "double-window.jpg",
            "Triple Window": "triple-window.jpg",
            "Quadruple Window": "quadruple-window.jpg",
            Other: "other-window.jpg",
          };
          return (
            <div
              key={tp.name}
              className="col-4 lg:col-2 cursor-pointer"
              onClick={() => onBlindTypeChange(tp.name)}
            >
              <div
                className={`item-frame ${
                  blindState.type === tp.name ? "active-frame" : ""
                }`}
              >
                <img
                  className="img-fluid img-item"
                  // eslint-disable-next-line no-undef
                  src={require(`../assets/img/gallery.img/${slugMap[tp.name]}`)}
                  alt={tp.name}
                />
                <div className="text-center item-name">{t(tp.name)}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-column mt-4">
        <div className="flex align-items-center">
          {t("Frame Width")}&nbsp;
          <strong className="text-help">{blindState.width}</strong>&nbsp;
          {t("Inches")}
          <Button
            className="p-button-help ml-2"
            icon="pi pi-info-circle"
            label={t("View Description")}
            onClick={() => setShowWidthDesc(true)}
          />
        </div>
        <InputNumber
          className="max-w-full lg:w-7rem"
          min={1}
          max={500}
          value={blindState.width}
          onChange={(e) => onBlindChange("width", e.value)}
          showButtons
          buttonLayout="horizontal"
          step={1}
          decrementButtonClassName="p-button-primary"
          incrementButtonClassName="p-button-primary"
          incrementButtonIcon="pi pi-plus"
          decrementButtonIcon="pi pi-minus"
        />
      </div>
      <div className="flex flex-column mt-3">
        <div className="flex align-items-center">
          {t("Height")}:&nbsp;
          <strong className="text-help">{blindState.height}</strong>&nbsp;
          {t("Inches")}
          <Button
            className="p-button-help ml-2"
            icon="pi pi-info-circle"
            label={t("View Description")}
            onClick={() => setShowHeightDesc(true)}
          />
        </div>
        <InputNumber
          className="max-w-full lg:w-7rem"
          min={1}
          max={500}
          value={blindState.height}
          onChange={(e) => onBlindChange("height", e.value)}
          showButtons
          buttonLayout="horizontal"
          step={1}
          decrementButtonClassName="p-button-primary"
          incrementButtonClassName="p-button-primary"
          incrementButtonIcon="pi pi-plus"
          decrementButtonIcon="pi pi-minus"
        />
      </div>
      <div className="flex flex-column mt-4">
        <div className="flex align-items-center mb-2">
          {t("Wall Mount")}
          <Button
            className="p-button-help ml-2"
            icon="pi pi-info-circle"
            label={t("View Description")}
            onClick={() => setShowMountDesc(true)}
          />
        </div>
        <div className="blind-mount-options">
          <div
            className={`blind-mount-option${blindState.wallMount === "Inside" ? " active" : ""}`}
            onClick={() => onBlindChange("wallMount", "Inside")}
          >
            <div className="blind-mount-icon">
              <svg viewBox="0 0 80 60" width="80" height="60">
                <rect x="10" y="5" width="60" height="50" fill="none" stroke="#888" strokeWidth="2" />
                <rect x="15" y="8" width="50" height="44" fill="#d4882b" opacity="0.3" />
                <line x1="15" y1="8" x2="15" y2="52" stroke="#d4882b" strokeWidth="2" />
                <line x1="65" y1="8" x2="65" y2="52" stroke="#d4882b" strokeWidth="2" />
              </svg>
            </div>
            <span>{t("Inside Mount")}</span>
          </div>
          <div
            className={`blind-mount-option${blindState.wallMount === "Outside" ? " active" : ""}`}
            onClick={() => onBlindChange("wallMount", "Outside")}
          >
            <div className="blind-mount-icon">
              <svg viewBox="0 0 80 60" width="80" height="60">
                <rect x="15" y="10" width="50" height="40" fill="none" stroke="#888" strokeWidth="2" />
                <rect x="5" y="3" width="70" height="54" fill="#d4882b" opacity="0.3" />
                <line x1="5" y1="3" x2="5" y2="57" stroke="#d4882b" strokeWidth="2" />
                <line x1="75" y1="3" x2="75" y2="57" stroke="#d4882b" strokeWidth="2" />
              </svg>
            </div>
            <span>{t("Outside Mount")}</span>
          </div>
          <div
            className={`blind-mount-option${blindState.wallMount === "Upside" ? " active" : ""}`}
            onClick={() => onBlindChange("wallMount", "Upside")}
          >
            <div className="blind-mount-icon">
              <svg viewBox="0 0 80 60" width="80" height="60">
                <rect x="10" y="14" width="60" height="42" fill="none" stroke="#888" strokeWidth="2" />
                <rect x="5" y="3" width="70" height="10" fill="#d4882b" opacity="0.6" />
                <rect x="15" y="17" width="50" height="36" fill="#d4882b" opacity="0.3" />
              </svg>
            </div>
            <span>{t("Upside Mount")}</span>
          </div>
        </div>
      </div>
      <div className="mt-3">
        <div>{t("Pull Cord / Control Side")}</div>
        <div className="flex gap-3 mt-2">
          <label className="flex align-items-center gap-2 cursor-pointer">
            <input type="radio" name="pullCord" checked={blindState.pullCordSide === "Right"} onChange={() => onBlindChange("pullCordSide", "Right")} />
            {t("Right")}
          </label>
          <label className="flex align-items-center gap-2 cursor-pointer">
            <input type="radio" name="pullCord" checked={blindState.pullCordSide === "Left"} onChange={() => onBlindChange("pullCordSide", "Left")} />
            {t("Left")}
          </label>
        </div>
        <div className="blind-mount-options mt-2">
          <div
            className={`blind-mount-option${blindState.pullCordSide === "Right" ? " active" : ""}`}
            onClick={() => onBlindChange("pullCordSide", "Right")}
          >
            <div className="blind-mount-icon">
              <svg viewBox="0 0 80 60" width="80" height="60">
                <rect x="10" y="5" width="60" height="40" fill="#d4882b" opacity="0.85" />
                <line x1="10" y1="15" x2="70" y2="15" stroke="#fff" strokeWidth="1" opacity="0.5" />
                <line x1="10" y1="25" x2="70" y2="25" stroke="#fff" strokeWidth="1" opacity="0.5" />
                <line x1="10" y1="35" x2="70" y2="35" stroke="#fff" strokeWidth="1" opacity="0.5" />
                <line x1="68" y1="5" x2="68" y2="55" stroke="#6b4423" strokeWidth="1.2" />
                <rect x="20" y="48" width="40" height="10" fill="none" stroke="#888" strokeWidth="1" />
              </svg>
            </div>
            <span>{t("Right")}</span>
          </div>
          <div
            className={`blind-mount-option${blindState.pullCordSide === "Left" ? " active" : ""}`}
            onClick={() => onBlindChange("pullCordSide", "Left")}
          >
            <div className="blind-mount-icon">
              <svg viewBox="0 0 80 60" width="80" height="60">
                <rect x="10" y="5" width="60" height="40" fill="#d4882b" opacity="0.85" />
                <line x1="10" y1="15" x2="70" y2="15" stroke="#fff" strokeWidth="1" opacity="0.5" />
                <line x1="10" y1="25" x2="70" y2="25" stroke="#fff" strokeWidth="1" opacity="0.5" />
                <line x1="10" y1="35" x2="70" y2="35" stroke="#fff" strokeWidth="1" opacity="0.5" />
                <line x1="12" y1="5" x2="12" y2="55" stroke="#6b4423" strokeWidth="1.2" />
                <rect x="20" y="48" width="40" height="10" fill="none" stroke="#888" strokeWidth="1" />
              </svg>
            </div>
            <span>{t("Left")}</span>
          </div>
        </div>
      </div>
      <div className="mt-3">
        <div>{t("Aluminum frame color")}</div>
        <div className="blind-mount-options mt-2">
          {[
            { name: "A suitable one", swatch: null },
            { name: "White", swatch: "#ffffff" },
            { name: "Black", swatch: "#222222" },
            { name: "Ash", swatch: "#9e9e9e" },
            { name: "Brown", swatch: "#6b4423" },
          ].map((c) => (
            <div
              key={c.name}
              className={`blind-mount-option${blindState.frameColor === c.name ? " active" : ""}`}
              onClick={() => onBlindChange("frameColor", c.name)}
            >
              <div className="blind-mount-icon flex justify-content-center align-items-center" style={{ height: 28 }}>
                {c.swatch ? (
                  <span
                    style={{
                      display: "inline-block",
                      width: 24,
                      height: 24,
                      borderRadius: 4,
                      background: c.swatch,
                      border: "1px solid #888",
                      boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.4)",
                    }}
                  />
                ) : (
                  <span style={{ fontSize: 18, color: "#888" }}>✓</span>
                )}
              </div>
              <span>{t(c.name)}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-3">
        <div>{t("Quantity")}</div>
        <InputNumber
          className="w-full lg:w-7rem mt-2"
          min={1}
          value={blindState.quantity}
          onChange={(e) => onBlindChange("quantity", e.value)}
          showButtons
          buttonLayout="horizontal"
          step={1}
          decrementButtonClassName="p-button-primary"
          incrementButtonClassName="p-button-primary"
          incrementButtonIcon="pi pi-plus"
          decrementButtonIcon="pi pi-minus"
        />
      </div>
      <div className="grid mt-3">
        <div className="col-12 lg:col-6">
          <div className="border-dashed border-500 p-3 flex flex-column gap-2">
            <strong>
              {t("Price per unit")} : LKR {numberWithCommas(blindState.cost)}
            </strong>
            {getEffectiveDiscount() && (
              <span>
                {getEffectiveDiscountLabel()} : - LKR {numberWithCommas(getEffectiveDiscountAmount(blindState.total))}
              </span>
            )}
            <strong>
              {t("Total")} : LKR {numberWithCommas(blindState.total - getEffectiveDiscountAmount(blindState.total))}
            </strong>
          </div>
          <Button
            className="surface-700 mt-3 w-full"
            label={t("Add to Cart")}
            icon="pi pi-shopping-cart"
            onClick={() => addToCart()}
          />
        </div>
      </div>
      <Dialog
        header={t("Frame Width")}
        visible={showWidthDesc}
        onHide={() => setShowWidthDesc(false)}
        className="w-full sm:w-30rem"
        modal
      >
        <div className="flex flex-column gap-3 p-3">
          <p className="m-0 text-sm sm:text-base">
            If you are installing this inside the frame, reduce one inch from the width of the frame; if you are installing it outside or upward of the frame, increase it by 2 inches
          </p>
          <p className="m-0 text-xs sm:text-sm text-color">
            මෙය ඔබ රාමුවේ ඇතුළට සවිකරන්නේනම් රාමුවේ පළලට වඩා අඟලක් අඩු කරන්න, රාමුවේ එළියට හෝ උඩු අතට සවිකරන්නේනම් අඟල් 2ක් වැඩි කරන්න
          </p>
          <div className="flex justify-content-end">
            <Button
              label={t("Close")}
              onClick={() => setShowWidthDesc(false)}
              className="p-button-text"
            />
          </div>
        </div>
      </Dialog>
      <Dialog
        header={t("Height")}
        visible={showHeightDesc}
        onHide={() => setShowHeightDesc(false)}
        className="w-full sm:w-30rem"
        modal
      >
        <div className="flex flex-column gap-3 p-3">
          <p className="m-0 text-sm sm:text-base">
            If you are installing this inside the frame, increase it by two inches more than the height of the frame, if you are installing it outside or upward of the frame, increase it by 6 inches.
          </p>
          <p className="m-0 text-xs sm:text-sm text-color">
            මෙය ඔබ රාමුවේ ඇතුළට සවිකරන්නේනම් රාමුවේ උසට වඩා අඟල් දෙකක් වැඩි කරන්න, රාමුවේ එළියට හෝ උඩු අතට සවිකරන්නේනම් අඟල් 6ක් වැඩි කරන්න.
          </p>
          <div className="flex justify-content-end">
            <Button
              label={t("Close")}
              onClick={() => setShowHeightDesc(false)}
              className="p-button-text"
            />
          </div>
        </div>
      </Dialog>
      <Dialog
        header={t("Wall Mount")}
        visible={showMountDesc}
        onHide={() => setShowMountDesc(false)}
        className="w-full sm:w-30rem"
        modal
      >
        <div className="flex flex-column gap-3 p-3">
          <p className="m-0 text-sm sm:text-base">
            The brackets here changes depending on where it is held; therefore, mention the required brackets below according to the capability of installing it on your frame.
          </p>
          <p className="m-0 text-xs sm:text-sm text-color">
            මෙහි අල්ලූව අල්ලන ස්ථානය අනූව වෙනස් වේ, එමනිසා ඔබගේ රාමුවේ සවිකිරීමට ඇති හැකියාව පරිදි අවශ්‍ය අල්ලූව පහතින් සඳහන් කරන්න.
          </p>
          <div className="flex justify-content-end">
            <Button
              label={t("Close")}
              onClick={() => setShowMountDesc(false)}
              className="p-button-text"
            />
          </div>
        </div>
      </Dialog>
    </div>
  );

  return (
    <div style={{ backgroundColor: "#fff" }}>
      <Dialog
        header="Item Added"
        visible={showDialog}
        onHide={() => setShowDialog(false)}
        closable={false}
        style={{ width: "500px", maxWidth: "90%" }}
        draggable={false}
      >
        <p>
          {t(
            "Selection added to the cart successfully. Do you want to continue"
          )}
          {t("shopping or go to the cart?")}
        </p>
        <div className="gap-2 flex">
          <button onClick={() => reset()} className="dialog-button">
            {t("Continue Shopping")}
          </button>
          <button onClick={() => navigate("/cart")} className="dialog-button">
            {t("Go to Cart")}
          </button>
        </div>
      </Dialog>
      <Dialog
        header="Type Limit Reached"
        visible={showLimitDialog}
        onHide={() => setShowLimitDialog(false)}
        closable={false}
        style={{ width: "500px", maxWidth: "90%" }}
        draggable={false}
      >
        <p>
          {t("You have reached the monthly limit of 15 times for this type. Please try again next month.")}
        </p>
        <div className="gap-2 flex justify-content-center">
          <button onClick={() => setShowLimitDialog(false)} className="dialog-button">
            {t("Close")}
          </button>
        </div>
      </Dialog>
      <div className="content-wrapper">
        <div className="grid">
          <div className="col-12 lg:col-5 lg:pr-3">
            <Button
              label="Back to store"
              icon="pi pi-angle-left"
              className="px-0 py-3"
              severity="secondary"
              onClick={() => navigate(state.selectedDesign?.SubCategory ? `/gallery/${state.selectedDesign.SubCategory}` : "/gallery")}
              text
            />
            <div>
              <div className="flex justify-content-between align-items-center">
                <h4 className="m-0">{state.selectedDesign?.Name}</h4>
                {(() => {
                  const sub = state.selectedDesign?.SubCategory;
                  const cat = state.selectedDesign?.Category;
                  const label = (sub && slugToName[sub]) || cat;
                  return label ? (
                    <span
                      style={{
                        fontFamily: "Cormorant Garamond",
                        color: "#b68d40",
                        fontSize: "1.5rem",
                        fontWeight: 600,
                      }}
                    >
                      {t(label)}
                    </span>
                  ) : null;
                })()}
              </div>
              <label>{state.selectedDesign?.Description}</label>
            </div>
            <div className="flex justify-content-between mt-3">
              <label>
                LKR {state.selectedDesign?.Price}.00{" "}
                {isInteriorItem()
                  ? t("per item")
                  : isBlindsItem()
                  ? t("per ft")
                  : t("per meter")}
              </label>
              <label>Item Code :{state.selectedDesign?.Code}</label>
            </div>
            <div className={`mt-1${isBlindsItem() ? " item-image-zoom" : ""}`}>
              <img
                className="item-image"
                src={getFirebaseImageUrl(state.image)}
                alt="Not Found"
              />
            </div>
          </div>
          {isInteriorItem() ? renderInteriorCalculator() : isBlindsItem() ? renderBlindsCalculator() : (
          <div className="col-12 lg:col-7 lg:pl-3">
            <h1 className="text-4xl lg:text-6xl line-height-1 mt-5 lg:mt-7">
              {t("Customize Selected Design")}
            </h1>
            <div className="flex align-items-center mt-3">
              {t("Type")}
              <Button
                className="p-button-help ml-2"
                icon="pi pi-info-circle"
                label={t("View Description")}
                onClick={() => setShowTypeDesc(true)}
              />
            </div>
            <div className="design-types row ">
              {!isDoubleCurtainItem() && (
                <div
                  className="col-4 lg:col-2 cursor-pointer"
                  onClick={() => onTypeChange("Door")}
                >
                  <div
                    className={`item-frame ${
                      state.type === "Door" ? "active-frame" : ""
                    }`}
                  >
                    <img
                      className="img-fluid img-item"
                      // eslint-disable-next-line no-undef
                      src={require("../assets/img/gallery.img/door.jpg")}
                      alt="Door"
                    />
                    <div className="text-center item-name">{t("Door")}</div>
                  </div>
                </div>
              )}
              <div
                className="col-4 lg:col-2 cursor-pointer"
                onClick={() => onTypeChange("Single Window")}
              >
                <div
                  className={`item-frame ${
                    state.type === "Single Window" ? "active-frame" : ""
                  }`}
                >
                  <img
                    className="img-fluid img-item"
                    // eslint-disable-next-line no-undef
                    src={require("../assets/img/gallery.img/single-window.jpg")}
                    alt="Single Window"
                  />
                  <div className="text-center item-name">
                    {t("Single Window")}
                  </div>
                </div>
              </div>
              <div
                className="col-4 lg:col-2 cursor-pointer"
                onClick={() => onTypeChange("Double Window")}
              >
                <div
                  className={`item-frame ${
                    state.type === "Double Window" ? "active-frame" : ""
                  }`}
                >
                  <img
                    className="img-fluid img-item"
                    // eslint-disable-next-line no-undef
                    src={require("../assets/img/gallery.img/double-window.jpg")}
                    alt="Double Window"
                  />
                  <div className="text-center item-name">
                    {t("Double Window")}
                  </div>
                </div>
              </div>
              <div
                className="col-4 lg:col-2 cursor-pointer"
                onClick={() => onTypeChange("Triple Window")}
              >
                <div
                  className={`item-frame ${
                    state.type === "Triple Window" ? "active-frame" : ""
                  }`}
                >
                  <img
                    className="img-fluid img-item"
                    // eslint-disable-next-line no-undef
                    src={require("../assets/img/gallery.img/triple-window.jpg")}
                    alt="Triple Window"
                  />
                  <div className="text-center item-name">
                    {t("Triple Window")}
                  </div>
                </div>
              </div>
              <div
                className="col-4 lg:col-2 cursor-pointer"
                onClick={() => onTypeChange("Quadruple Window")}
              >
                <div
                  className={`item-frame ${
                    state.type === "Quadruple Window" ? "active-frame" : ""
                  }`}
                >
                  <img
                    className="img-fluid img-item"
                    // eslint-disable-next-line no-undef
                    src={require("../assets/img/gallery.img/quadruple-window.jpg")}
                    alt="Quadruple Window"
                  />
                  <div className="text-center item-name">
                    {t("Quadruple Window")}
                  </div>
                </div>
              </div>
              <div
                className="col-4 lg:col-2 cursor-pointer"
                onClick={() => onTypeChange("Other")}
              >
                <div
                  className={`item-frame ${
                    state.type === "Other" ? "active-frame" : ""
                  }`}
                >
                  <img
                    className="img-fluid img-item"
                    // eslint-disable-next-line no-undef
                    src={require("../assets/img/gallery.img/other-window.jpg")}
                    alt="Other"
                  />
                  <div className="text-center item-name">{t("Other")}</div>
                </div>
              </div>
            </div>
            <Dialog
              header={t("Type")}
              visible={showTypeDesc}
              onHide={() => setShowTypeDesc(false)}
              className="w-full sm:w-30rem"
              modal
            >
              <div className="flex flex-column gap-3 p-3">
                <p className="m-0 text-sm sm:text-base text-color-secondary">
                  {t("The width of door & windows are predicted as below")}
                </p>
                <p className="m-0 text-sm sm:text-base text-color-secondary">
                  {t(
                    "Depending on the size of the door and window frame, add or"
                  )}{" "}
                  {t("subtract the value in the Width box.")}
                </p>
                <div className="flex justify-content-end">
                  <Button
                    label={t("Close")}
                    onClick={() => setShowTypeDesc(false)}
                    className="p-button-text"
                  />
                </div>
              </div>
            </Dialog>
            <div className="flex flex-column mt-2">
              <div className="flex align-items-center">
                {t("Frame Width")}:&nbsp;
                <strong className="text-help">{state.width}</strong>&nbsp;
                {t("Inches")}
                <Button
                  className="p-button-help"
                  icon="pi pi-info-circle"
                  label={t("View Description")}
                  onClick={() => setShowWidthDesc(true)}
                />
              </div>
              <Dialog
                header={t("Width Information")}
                visible={showWidthDesc}
                onHide={() => setShowWidthDesc(false)}
                className="w-full sm:w-30rem"
                modal
              >
                <div className="flex flex-column gap-3 p-3">
                  <p className="m-0 text-sm sm:text-base text-color-secondary">
                    Apply only the width of the frame (we will increase the
                    required amount).
                    <br />
                    <br />
                    <span className="text-xs sm:text-sm text-color">
                      රමුවේ පලළ පමණක් යොදන්න (අවශ්‍ය ප්‍රමනය අප විසින් වැඩි කරනු
                      ලැබේ)
                    </span>
                  </p>
                  <div className="flex justify-content-end">
                    <Button
                      label={t("Close")}
                      onClick={() => setShowWidthDesc(false)}
                      className="p-button-text"
                    />
                  </div>
                </div>
              </Dialog>
              <InputNumber
                className="max-w-full lg:w-7rem"
                min={0}
                max={500}
                id="width"
                name="width"
                value={state.width}
                onChange={(e) => onChange("width", e.value)}
                showButtons
                buttonLayout="horizontal"
                step={1}
                decrementButtonClassName="p-button-primary"
                incrementButtonClassName="p-button-primary"
                incrementButtonIcon="pi pi-plus"
                decrementButtonIcon="pi pi-minus"
              />
            </div>
            <div className="flex flex-column mt-3">
              <div className="flex align-items-center">
                {t("Height")}:&nbsp;
                <strong className="text-help">{state.height}</strong>
                &nbsp; {t("Inches")}
                <span>
                  <Button
                    className="p-button-help"
                    icon="pi pi-info-circle"
                    label={t("View Description")}
                    onClick={() => setShowHeightDesc(true)}
                  />
                </span>
              </div>
              <Dialog
                header={t("Height Information")}
                visible={showHeightDesc}
                onHide={() => setShowHeightDesc(false)}
                className="w-full sm:w-30rem"
                modal
              >
                <div className="flex flex-column gap-3 p-3">
                  <p className="m-0 text-sm sm:text-base text-color-secondary">
                    Apply the height you want (There is no difference in price
                    from 48 inches to 102 inches in height).
                    <br />
                    <br />
                    <span className="text-xs sm:text-sm text-color">
                      ඔබට අවශ්‍ය උස යොදන්න (අගල් 48 සිට අගල් 102 දක්වා මිලෙහි
                      වෙනසක් නැත).
                    </span>
                  </p>
                  <div className="flex justify-content-end">
                    <Button
                      label={t("Close")}
                      onClick={() => setShowHeightDesc(false)}
                      className="p-button-text"
                    />
                  </div>
                </div>
              </Dialog>
              <InputNumber
                className="max-w-full lg:w-7rem"
                min={0}
                max={120}
                id="height"
                name="height"
                value={state.height}
                onChange={(e) => onChange("height", e.value, false)}
                showButtons
                buttonLayout="horizontal"
                step={1}
                decrementButtonClassName="p-button-primary"
                incrementButtonClassName="p-button-primary"
                incrementButtonIcon="pi pi-plus"
                decrementButtonIcon="pi pi-minus"
              />
            </div>
            <div className="flex flex-column mt-3">
              <div className="el-switch flex align-items-center">
                <label className="el-label-left">{t("Without Pole")}</label>
                <InputSwitch
                  style={{ color: "#656565", marginLeft: "10px" }}
                  checked={state.isPole}
                  onChange={onPoleChange}
                />
                <label className="el-label-right ml-3">
                  {t("With Pole")}
                </label>
                <Button
                  className="p-button-help ml-2"
                  icon="pi pi-info-circle"
                  label={t("View Description")}
                  onClick={() => setShowPoleDesc(true)}
                />
              </div>
            </div>
            <div className="flex flex-column mt-3">
              <div className="flex align-items-center">
                {t("Number of Pleats")}:&nbsp;
                <strong className="text-help">{state.pleats}</strong>
                {isDoubleCurtainItem() && (
                  <span className="ml-2 text-color-secondary text-sm">
                    ({t("Front")}: {state.frontPleats}, {t("Back")}: {state.backPleats})
                  </span>
                )}
                <span>
                  <Button
                    className="p-button-help"
                    icon="pi pi-info-circle"
                    label={t("View Description")}
                    onClick={() => setShowPleatsDesc(true)}
                  />
                </span>
              </div>
              <Dialog
                header={t("Pleats Adjustment")}
                visible={showPleatsDesc}
                onHide={() => setShowPleatsDesc(false)}
                className="w-full sm:w-30rem"
                modal
              >
                <div className="flex flex-column gap-3 p-3">
                  <p className="m-0 text-sm sm:text-base text-color-secondary">
                    Increase or decrease this as per your ability
                    <br />
                    <br />
                    <span className="text-xs sm:text-sm text-color">
                      ඔබගේ හැකියාව පරිදි මෙය වෙනස් කරගන්න
                    </span>
                  </p>
                  <div className="flex justify-content-end">
                    <Button
                      label={t("Close")}
                      onClick={() => setShowPleatsDesc(false)}
                      className="p-button-text"
                    />
                  </div>
                </div>
              </Dialog>
              <Dialog
                header={t("Poles")}
                visible={showPoleDesc}
                onHide={() => setShowPoleDesc(false)}
                className="w-full sm:w-30rem"
                modal
              >
                <div className="flex flex-column gap-3 p-3">
                  <p className="m-0 text-sm sm:text-base text-color-secondary">
                    {t(
                      "For the purpose of curtain brackets of or own designs have been"
                    )}{" "}
                    {t(
                      "utilized to date and it is made of standard galvanized tubes and"
                    )}{" "}
                    {t(
                      "quality wood. A long time warranty is granted and the benefits"
                    )}{" "}
                    {t("you enjoy are,")}
                  </p>
                  <ul className="m-0 pl-4">
                    <li>{t("As we charge only the production cost the prices prevail at a low rate.")}</li>
                    <li>{t("As the bracket is provided with screws for installing facility and It is convenient to fix on the wall.")}</li>
                    <li>{t("Depending on the location of walls and windows our brackets consists of various shaped accessories.")}</li>
                    <li>{t("The fastener is furnished with a good quality wood (with no adhesive) the security is high.")}</li>
                    <li>{t("It is long lasting")}</li>
                    <li>{t("Attractive and available in many colours")}</li>
                    <li>{t("Being able to contribute to saving the environment as less wood is used.")}</li>
                  </ul>
                  <div className="flex justify-content-end">
                    <Button
                      label={t("Close")}
                      onClick={() => setShowPoleDesc(false)}
                      className="p-button-text"
                    />
                  </div>
                </div>
              </Dialog>
              {isDoubleCurtainItem() ? (
                <div className="flex flex-column gap-3 mt-2">
                  <div className="flex flex-column">
                    <label>
                      {t("Front Curtain")}
                      <Button
                        className="p-button-help ml-2"
                        icon="pi pi-info-circle"
                        label={t("View Description")}
                        onClick={() => setShowPleatsDesc(true)}
                      />
                    </label>
                    <InputNumber
                      className="max-w-full lg:w-7rem"
                      min={1}
                      id="frontPleats"
                      name="frontPleats"
                      value={state.frontPleats}
                      onChange={(e) => onChange("frontPleats", e.value, false)}
                      showButtons
                      buttonLayout="horizontal"
                      step={1}
                      decrementButtonClassName="p-button-primary"
                      incrementButtonClassName="p-button-primary"
                      incrementButtonIcon="pi pi-plus"
                      decrementButtonIcon="pi pi-minus"
                    />
                  </div>
                  <div className="flex flex-column">
                    <label>
                      {t("Back Curtain")}
                      <Button
                        className="p-button-help ml-2"
                        icon="pi pi-info-circle"
                        label={t("View Description")}
                        onClick={() => setShowPleatsDesc(true)}
                      />
                    </label>
                    <InputNumber
                      className="max-w-full lg:w-7rem"
                      min={1}
                      id="backPleats"
                      name="backPleats"
                      value={state.backPleats}
                      onChange={(e) => onChange("backPleats", e.value, false)}
                      showButtons
                      buttonLayout="horizontal"
                      step={1}
                      decrementButtonClassName="p-button-primary"
                      incrementButtonClassName="p-button-primary"
                      incrementButtonIcon="pi pi-plus"
                      decrementButtonIcon="pi pi-minus"
                    />
                  </div>
                </div>
              ) : (
                <div className="recommended-div">
                  <InputNumber
                    className="max-w-full"
                    min={1}
                    id="pleats"
                    name="pleats"
                    value={state.pleats}
                    onChange={(e) => onChange("pleats", e.value, false)}
                    showButtons
                    buttonLayout="horizontal"
                    step={1}
                    decrementButtonClassName="p-button-primary"
                    incrementButtonClassName="p-button-primary"
                    incrementButtonIcon="pi pi-plus"
                    decrementButtonIcon="pi pi-minus"
                  />
                  <div className="recommended-div-2">
                    <label className="text-help">
                      {t("Recommended")} : {state.selectedDesign?.Category === "Ripple Curtains" ? getRecommendedRipple() : getRecommended()}
                    </label>
                  </div>
                </div>
              )}
            </div>
            <br />
            <div className="grid">
              <div className="col-12 lg:col-7 gap-2 mt-2">
                <div className="mt-3">
                  <div>{t("Quantity")}</div>
                  <InputNumber
                    className="w-full lg:w-7rem mt-2"
                    min={1}
                    id="quantity"
                    name="quantity"
                    value={state.quantity}
                    onChange={(e) => onChange("quantity", e.value, false)}
                    showButtons
                    buttonLayout="horizontal"
                    step={1}
                    decrementButtonClassName="p-button-primary"
                    incrementButtonClassName="p-button-primary"
                    incrementButtonIcon="pi pi-plus"
                    decrementButtonIcon="pi pi-minus"
                  />
                </div>
              </div>
              <div className="col-12 lg:col-5 mt-4">
                <div className="border-dashed border-500 justify-content-center p-2 flex flex-column gap-2">
                  <strong>
                    {t("Price per unit")} : LKR {numberWithCommas(state.cost)}
                  </strong>
                  {getEffectiveDiscount() && (
                    <span>
                      {getEffectiveDiscountLabel()} : - LKR {numberWithCommas(getEffectiveDiscountAmount(state.total))}
                    </span>
                  )}
                  <strong>
                    {t("Total")} : LKR {numberWithCommas(state.total - getEffectiveDiscountAmount(state.total))}
                  </strong>
                </div>
                <div>
                  <Button
                    className="surface-700 mt-3 w-full"
                    label={t("Add to Cart")}
                    icon="pi pi-shopping-cart"
                    onClick={() => addToCart()}
                  />
                </div>
              </div>
            </div>
          </div>
          )}
        </div>
        {!isBlindsItem() && !isInteriorItem() && (
        <div className="mt-6">
          <h1 className="text-7xl mb-4 text-center">{t("User Guide")}</h1>
          <section>
            <h5 className="text-2xl">{t("How to measure?")}</h5>
            <div className="grid mt-3">
              <div className="col-12 lg:col-6 text-center">
                <img
                  className="img-measure w-1/4"
                  // eslint-disable-next-line no-undef
                  src={require("../assets/img/gallery.img/how-to-measure.jpg")}
                  alt="Measurement Guide"
                />
              </div>
              <div className="col-12 lg:col-6 lg:px-4">
                <h5 style={{ fontSize: "20px" }}>
                  {t("All measurements should be in inches")} <br />
                </h5>
                <div className="measurement-points">
                  <ul
                    style={{
                      gap: "20px",
                      paddingLeft: "0",
                    }}
                  >
                    <div className="measurement-list">
                      <div className="measurment-list-order">
                        <li>A</li>
                      </div>
                      <div>
                        <li>{t("Width of the Window/Door outer frame")}</li>
                      </div>
                    </div>
                    <div className="measurement-list">
                      <div className="measurment-list-order">
                        <li>B</li>
                      </div>
                      <div>
                        <li>{t("Height of the Window/Door outer frame")}</li>
                      </div>
                    </div>
                    <div className="measurement-list">
                      <div className="measurment-list-order">
                        <li>C</li>
                      </div>
                      <div>
                        <li>
                          {t(
                            "Measurement from the floor to the bottom of the window"
                          )}
                          {t("frame")}
                        </li>
                      </div>
                    </div>
                    <div className="measurement-list">
                      <div className="measurment-list-order">
                        <li>D</li>
                      </div>
                      <div>
                        <li>
                          {t(
                            "Measurement from the ceiling/ roof/ slab to the top of"
                          )}
                          {t("the window frame")}
                        </li>
                      </div>
                    </div>
                    <div className="measurement-list">
                      <div className="measurment-list-order">
                        <li>E</li>
                      </div>
                      <div>
                        <li>
                          {t(
                            "Full height from the floor to the ceiling/ slab/ roof"
                          )}
                          {t("(Not essential)")}
                        </li>
                      </div>
                    </div>
                  </ul>
                </div>
              </div>
            </div>
          </section>
          <h5 className="text-2xl mt-6">{t("Type & Width")}</h5>
          <div>
            <p>{t("The width of door & windows are predicted as below")}</p>
            <div className="design-types row">
              <div className="col-12 lg:col-4 flex justify-content-center">
                <img
                  className="img-measure img-fluid "
                  // eslint-disable-next-line no-undef
                  src={require("../assets/img/gallery.img/door-type.jpg")}
                  alt="Width 1"
                />
              </div>
              <div className="col-12 lg:col-4 flex justify-content-center">
                <img
                  className="img-measure img-fluid"
                  // eslint-disable-next-line no-undef
                  src={require("../assets/img/gallery.img/single-type.jpg")}
                  alt="Width 2"
                />
              </div>
              <div className="col-12 lg:col-4 flex justify-content-center">
                <img
                  className="img-measure img-fluid"
                  // eslint-disable-next-line no-undef
                  src={require("../assets/img/gallery.img/double-type.jpg")}
                  alt="Width 3"
                />
              </div>
              <div className="col-12 lg:col-4 flex justify-content-center">
                <img
                  className="img-measure img-fluid"
                  // eslint-disable-next-line no-undef
                  src={require("../assets/img/gallery.img/triple-type.jpg")}
                  alt="Width 4"
                />
              </div>
              <div className="col-12 lg:col-4 flex justify-content-center">
                <img
                  className="img-measure img-fluid"
                  // eslint-disable-next-line no-undef
                  src={require("../assets/img/gallery.img/quadruple-type.jpg")}
                  alt="Width 5"
                />
              </div>
              <div className="col-12 lg:col-4 flex justify-content-center">
                <img
                  className="img-measure img-fluid"
                  // eslint-disable-next-line no-undef
                  src={require("../assets/img/gallery.img/other-type.jpg")}
                  alt="Width 6"
                />
              </div>
            </div>
            <div>
              <p className="flex justify-content-center">
                {t(
                  "Depending on the size of the door and window frame, add or"
                )}
                {t("subtract the value in the Width box.")}
              </p>
              <div
                className="content-wrapper"
                style={{
                  borderradius: "8px",
                  border: "1px dashed #656565",
                  width: "350px",
                  height: "100px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexdirection: "row",
                    justifyContent: "center",
                    alignContent: "center",
                  }}
                >
                  {t("Frame Width")} ({t("Inches")})
                  <span>
                    <Button
                      className="p-button-help"
                      icon="pi pi-info-circle"
                      label="View Description"
                    />
                  </span>
                </div>
                <div className="flex justify-content-center">
                  <img
                    style={{
                      width: "200px",
                      height: "50px",
                    }}
                    // eslint-disable-next-line no-undef
                    src={require("../assets/img/gallery.img/width-frame.jpg")}
                    alt="Width frame"
                  />
                </div>
              </div>
            </div>
            <p className="flex justify-content-center">
              {t(
                "Simply specify the inside width of your window frame and we will"
              )}
              {t("increase it by 6 inches on each side.")}
            </p>
          </div>
          <h5 className="text-2xl mt-6">{t("Height")}</h5>
          <div
            className="grid"
            style={{
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div className="col-12 lg:col-5 lg:px-4 ">
              <img
                className="item-image-2"
                // eslint-disable-next-line no-undef
                src={require("../assets/img/gallery.img/height-frame.jpg")}
                alt="Height Guide"
              />
            </div>
            <div className="col-12 lg:col-7 lg:px-4">
              <p style={{ fontSize: "20px" }}>
                {t(
                  "It is adequate to mention the necessary height in inches and the"
                )}
                {t("cost differs according to three sizes of height.")}
              </p>
              <div>
                <div className="height-details">
                  {t(
                    "1. Less than 47 inches (this reduce a half of the cost of"
                  )}
                  {t("material in your design) Conditions Apply")}
                </div>
                <div className="height-details">
                  {t("2. From 48 inches up to 102 inches")}
                </div>
                <div className="height-details">
                  {t("3. From 103 inches to above")}
                </div>
                <div className="height-details">{t("Notice")}:</div>
                <div className="height-details">
                  {t(
                    "If 100 inches height is maintained a fantastic finish is"
                  )}
                  {t("obtainable for your design")}
                </div>
              </div>
              <div className="height-measurement-div content-wrapper">
                <div
                  style={{
                    display: "flex",
                    flexdirection: "row",
                    justifyContent: "center",
                    alignContent: "center",
                  }}
                >
                  {t("Height")} ({t("Inches")})
                  <span>
                    <Button
                      className="p-button-help"
                      icon="pi pi-info-circle"
                      label="View Description"
                    />
                  </span>
                </div>
                <div className="flex justify-content-center">
                  <img
                    style={{
                      width: "200px",
                      height: "50px",
                    }}
                    // eslint-disable-next-line no-undef
                    src={require("../assets/img/gallery.img/height-mesurement-frame.jpg")}
                    alt="pleat frame"
                  />
                </div>
              </div>
            </div>
          </div>
          <h5 className="text-2xl mt-6">{t("Pleats")}</h5>
          <div
            style={{
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div>
              <p>
                {t(
                  "Usual practice is inserting 3 pleats per foot (The International"
                )}
                {t(
                  "Standards is 14 inch gap between two pleats). Accordingly, a"
                )}
                {t("quantity of material is been used,")}
              </p>
            </div>
            <div className="grid">
              <div className="col-12 lg:col-6 lg:px-4 flex justify-content-center">
                <img
                  className="item-image-2"
                  // eslint-disable-next-line no-undef
                  src={require("../assets/img/gallery.img/pleats-frame-1.jpg")}
                  alt="pleats Guide"
                />
              </div>
              <div className="col-12 lg:col-6 lg:px-4">
                <div className="grid flex justify-content-center mt-6">
                  <img
                    className="col-6 lg:col-4"
                    // eslint-disable-next-line no-undef
                    src={require("../assets/img/gallery.img/pleats-frame-2.jpg")}
                    alt="pleats frame 2"
                  />
                  <img
                    className="col-6 lg:col-4"
                    // eslint-disable-next-line no-undef
                    src={require("../assets/img/gallery.img/pleats-frame-3.jpg")}
                    alt="pleats frame 3"
                  />
                  <img
                    className="col-6 lg:col-4"
                    // eslint-disable-next-line no-undef
                    src={require("../assets/img/gallery.img/pleats-frame-4.jpg")}
                    alt="pleats frame 4"
                  />
                  <img
                    className="col-6 lg:col-4"
                    // eslint-disable-next-line no-undef
                    src={require("../assets/img/gallery.img/pleats-frame-5.jpg")}
                    alt="pleats frame 5"
                  />
                  <img
                    className="col-6 lg:col-4"
                    // eslint-disable-next-line no-undef
                    src={require("../assets/img/gallery.img/pleats-frame-6.jpg")}
                    alt="pleats frame 6"
                  />
                </div>
              </div>
            </div>
            <div className="pleats-design">
              <p>
                {t(
                  "The number of pleats can be decreased or increased according to"
                )}
                {t(
                  "your probability and requirement. The price depends with number"
                )}
                {t("of pleats on your decision.")}
              </p>
            </div>
            <div className="pleats-design">{t("Notice")}:</div>
            <div className="pleats-design" style={{ marginLeft: "30px" }}>
              {t(
                "If transparent curtains (sheer) are used for your window frames it"
              )}
              {t("is appropriate to increase a few number of pleats")}
            </div>
            <div
              className="content-wrapper mt-4"
              style={{
                borderradius: "8px",
                border: "1px dashed #656565",
                width: "350px",
                height: "100px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexdirection: "row",
                  justifyContent: "center",
                  alignContent: "center",
                }}
              >
                {t("Number of Pleats")}
                <span>
                  <Button
                    className="p-button-help"
                    icon="pi pi-info-circle"
                    label="View Description"
                  />
                </span>
              </div>
              <div className="flex justify-content-center">
                <img
                  style={{
                    width: "200px",
                    height: "50px",
                  }}
                  // eslint-disable-next-line no-undef
                  src={require("../assets/img/gallery.img/pleats-frame-7.jpg")}
                  alt="Width frame"
                />
              </div>
            </div>
          </div>
          <h5 className="text-2xl mt-6">{t("Poles")}</h5>
          <div
            style={{
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div>
              <p>
                {t(
                  "For the purpose of curtain brackets of or own designs have been"
                )}
                {t(
                  "utilized to date and it is made of standard galvanized tubes and"
                )}
                {t(
                  "quality wood. A long time warranty is granted and the benefits"
                )}
                {t("you enjoy are,")}
              </p>
            </div>
            <div className="pole-design">
              {t(
                "As we charge only the production cost the prices prevail at a low"
              )}
              {t("rate.")}
            </div>
            <div className="pole-design">
              {t(
                "As the bracket is provided with screws for installing facility and"
              )}
              {t("It is convenient to fix on the wall.")}
            </div>
            <div className="pole-design">
              {t("Depending on the location of walls and windows our brackets")}
              {t("consists of various shaped accessories.")}
            </div>
            <div className="pole-design">
              {t("The fastener is furnished with a good quality wood (with no")}
              {t("adhesive) the security is high.")}
            </div>
            <div className="pole-design">{t("It is long lasting")}</div>
            <div className="pole-design">
              {t("Attractive and available in many colours")}
            </div>
            <div className="pole-design">
              {t(
                "Being able to contribute to saving the environment as less wood is"
              )}
              {t("used.")}
            </div>
            <div
              className="grid"
              style={{
                marginTop: "30px",
                display: "flex",
                flexdirection: "row",
              }}
            >
              <div className="col-12 lg:col-5 flex justify-content-center">
                <img
                  // eslint-disable-next-line no-undef
                  src={require("../assets/img/gallery.img/pole-frame.jpg")}
                  alt="pole frame"
                />
              </div>
              <div className="col-12 lg:col-7">
                <div>
                  {t(
                    "As this is our own design this is not available in the open"
                  )}
                  {t(
                    "market & accessories such as poles are not issued on your"
                  )}
                  {t("request.")}
                </div>
                <div style={{ marginTop: "20px" }}>
                  {t("You can select from here whether you need or not a pole")}
                </div>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default Item;
